import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "lib/utils";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  Check,
  Equal,
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  User,
  Users,
  X,
} from "lucide-react";

import { useUpdateOrganizationCredits } from "hooks/useOrganizations";

import { Button } from "js/components/ui/button";

import type { Advisor, Credits } from "js/pages/Organization/types";

/* ------------------------------------------------------------------ */
/*  Types & constants                                                  */
/* ------------------------------------------------------------------ */

interface TeamSectionProps {
  advisors: Advisor[];
  orgCredits: Credits;
  onManageClick?: () => void;
  isLoading: boolean;
  isOwner?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onMemberClick?: (advisorEmail: string) => void;
}

const CREDIT_TYPE_LABELS: Partial<Record<keyof Credits, string>> = {
  pf: "Persona Física",
  pm: "Persona Moral",
};

const CREDIT_TYPE_SHORT: Partial<Record<keyof Credits, string>> = {
  pf: "PF",
  pm: "PM",
};

const ALL_CREDIT_KEYS = Object.keys(CREDIT_TYPE_LABELS) as QuotaFields[];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

type QuotaFields = "pf" | "pm";

function buildInitialAllocations(
  advisors: Advisor[],
): Record<string, Record<QuotaFields, number>> {
  const initial: Record<string, Record<QuotaFields, number>> = {};
  for (const advisor of advisors) {
    initial[advisor.id] = {
      pf: advisor.credits.pf ?? 0,
      pm: advisor.credits.pm ?? 0,
    };
  }
  return initial;
}


/* ------------------------------------------------------------------ */
/*  Inline stepper (edit mode cell)                                    */
/* ------------------------------------------------------------------ */

function CellStepper({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-lg border border-border/60 bg-background shadow-sm">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={value <= 0}
        className="flex size-8 items-center justify-center text-muted-foreground hover:bg-muted/50 hover:text-foreground disabled:opacity-30 rounded-l-lg transition-colors"
      >
        <Minus className="size-3" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(Math.max(0, n));
          else if (e.target.value === "") onChange(0);
        }}
        className="w-11 border-x border-border/60 bg-transparent py-1 text-center text-sm font-medium tabular-nums text-foreground outline-none"
      />
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="flex size-8 items-center justify-center text-muted-foreground hover:bg-muted/50 hover:text-foreground rounded-r-lg transition-colors"
      >
        <Plus className="size-3" />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

function TeamSection({
  advisors,
  orgCredits,
  isLoading,
  isOwner = false,
  isExpanded = false,
  onToggleExpand,
  onMemberClick,
}: TeamSectionProps) {
  const updateCreditsMutation = useUpdateOrganizationCredits();

  /* ── Edit mode state ─────────────────────────────────────── */
  const [isEditing, setIsEditing] = useState(false);
  const [selectedType, setSelectedType] = useState<QuotaFields>("pf");
  const [allocations, setAllocations] = useState<
    Record<string, Record<QuotaFields, number>>
  >({});

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "pf" | "pm" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [roleFilter, setRoleFilter] = useState<"all" | "Owner" | "Advisor">("all");

  const visibleTypes = useMemo(
    () =>
      ALL_CREDIT_KEYS.filter((k) => {
        const orgPool = orgCredits[k] ?? 0;
        const advisorTotal = advisors.reduce(
          (sum, adv) => sum + (adv.credits[k] ?? 0),
          0,
        );
        return orgPool + advisorTotal > 0;
      }),
    [orgCredits, advisors],
  );

  /* ── Unallocated credits (view mode) ───────────────────── */
  const unallocated = useMemo(() => {
    const totalPf = orgCredits.pf ?? 0;
    const totalPm = orgCredits.pm ?? 0;
    const allocPf = advisors.reduce((s, a) => s + (a.credits.pf ?? 0), 0);
    const allocPm = advisors.reduce((s, a) => s + (a.credits.pm ?? 0), 0);
    return {
      pf: totalPf - allocPf,
      pm: totalPm - allocPm,
    };
  }, [advisors, orgCredits]);

  /* ── Filtered advisors (search + role) ─────────────────── */
  const filteredAdvisors = useMemo(() => {
    let result = advisors;
    if (roleFilter !== "all") {
      result = result.filter((a) => a.role === roleFilter);
    }
    if (!searchQuery.trim()) return result;
    const q = searchQuery.trim().toLowerCase();
    return result.filter(
      (a) =>
        `${a.name} ${a.first_surname}`.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q),
    );
  }, [advisors, searchQuery, roleFilter]);

  /* ── Sort handler ────────────────────────────────────────── */
  const handleSort = (column: "name" | "pf" | "pm") => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir(column === "name" ? "asc" : "desc");
    }
  };

  /* ── Sorted advisors (view mode) ─────────────────────────── */
  const sortedAdvisors = useMemo(() => {
    if (!sortBy) return filteredAdvisors;
    const sorted = [...filteredAdvisors].sort((a, b) => {
      if (sortBy === "name") {
        const nameA = `${a.name} ${a.first_surname}`.toLowerCase();
        const nameB = `${b.name} ${b.first_surname}`.toLowerCase();
        return nameA.localeCompare(nameB);
      }
      const valA = a.credits[sortBy] ?? 0;
      const valB = b.credits[sortBy] ?? 0;
      return valA - valB;
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
  }, [filteredAdvisors, sortBy, sortDir]);

  const prevIsEditingRef = useRef(false);
  useEffect(() => {
    if (isEditing && !prevIsEditingRef.current) {
      setAllocations(buildInitialAllocations(advisors));
      setSelectedType((prev) =>
        visibleTypes.includes(prev) ? prev : (visibleTypes[0] ?? "pf"),
      );
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, advisors, visibleTypes]);

  /* ── Distribution calculations ──────────────────────────── */
  // Grand total = org pool + existing advisor allocations
  const orgTotal = useMemo(() => {
    const orgPool = orgCredits[selectedType] ?? 0;
    const existingAdvisorTotal = advisors.reduce(
      (sum, adv) => sum + (adv.credits[selectedType] ?? 0),
      0,
    );
    return orgPool + existingAdvisorTotal;
  }, [orgCredits, advisors, selectedType]);

  const totalAllocated = Object.values(allocations).reduce(
    (sum, credits) => sum + (credits[selectedType] ?? 0),
    0,
  );
  const remaining = orgTotal - totalAllocated;
  const isOver = totalAllocated > orgTotal;
  const ratio = orgTotal > 0 ? Math.min(totalAllocated / orgTotal, 1) : 0;

  const hasAnyOverAllocation = useMemo(() => {
    if (!isEditing) return false;
    for (const type of ALL_CREDIT_KEYS) {
      const orgPool = orgCredits[type] ?? 0;
      const existingAdvisorTotal = advisors.reduce(
        (sum, adv) => sum + (adv.credits[type] ?? 0),
        0,
      );
      const grandTotal = orgPool + existingAdvisorTotal;
      if (grandTotal === 0) continue;
      const allocated = Object.values(allocations).reduce(
        (sum, credits) => sum + (credits[type] ?? 0),
        0,
      );
      if (allocated > grandTotal) return true;
    }
    return false;
  }, [allocations, orgCredits, advisors, isEditing]);

  /* ── Handlers ───────────────────────────────────────────── */
  const handleAllocationChange = (advisorId: string, value: number) => {
    setAllocations((prev) => ({
      ...prev,
      [advisorId]: {
        ...prev[advisorId],
        [selectedType]: Math.max(0, Math.floor(value)),
      },
    }));
  };

  const handleDistributeEqually = () => {
    if (advisors.length === 0) return;
    const perAdvisor = Math.floor(orgTotal / advisors.length);
    const newAllocations = { ...allocations };
    for (const advisor of advisors) {
      newAllocations[advisor.id] = {
        ...newAllocations[advisor.id],
        [selectedType]: perAdvisor,
      };
    }
    setAllocations(newAllocations);
  };

  const handleReset = () => {
    setAllocations(buildInitialAllocations(advisors));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAllocations({});
  };

  const handleSave = () => {
    const orgRemainingQuotes: Record<string, number> = {};
    for (const type of ALL_CREDIT_KEYS) {
      const orgPool = orgCredits[type] ?? 0;
      // Grand total = org pool + what advisors already have
      const existingAdvisorTotal = advisors.reduce(
        (sum, adv) => sum + (adv.credits[type] ?? 0),
        0,
      );
      const grandTotal = orgPool + existingAdvisorTotal;
      // New allocations from the form
      const newAllocated = Object.values(allocations).reduce(
        (sum, credits) => sum + (credits[type] ?? 0),
        0,
      );
      orgRemainingQuotes[type] = grandTotal - newAllocated;
    }
    // Pass through tenant credits unchanged
    orgRemainingQuotes["tenant_pf"] = orgCredits.tenant_pf ?? 0;
    orgRemainingQuotes["tenant_pm"] = orgCredits.tenant_pm ?? 0;

    const payload = {
      organization: { quotes: orgRemainingQuotes },
      advisors: advisors.map((advisor) => ({
        id: advisor.id,
        quotes: {
          pf: allocations[advisor.id]?.pf ?? advisor.credits.pf ?? 0,
          pm: allocations[advisor.id]?.pm ?? advisor.credits.pm ?? 0,
          tenant_pf: advisor.credits.tenant_pf ?? 0,
          tenant_pm: advisor.credits.tenant_pm ?? 0,
        },
      })),
    };

    updateCreditsMutation.mutate(payload as any, {
      onSuccess: () => {
        setIsEditing(false);
        setAllocations({});
      },
    });
  };

  /* ── Loading skeleton ───────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card p-5">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="size-8 animate-pulse rounded-full bg-muted" />
              <div className="flex flex-1 flex-col gap-1">
                <div className="h-3 w-28 animate-pulse rounded bg-muted" />
                <div className="h-2.5 w-40 animate-pulse rounded bg-muted" />
              </div>
              <div className="h-5 w-10 animate-pulse rounded bg-muted" />
              <div className="h-5 w-10 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border/60 bg-card transition-all duration-300"
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-1">
        <h3 className="text-base font-semibold text-foreground">
          Tu equipo{" "}
          <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
            {advisors.length}
          </span>
        </h3>
        <div className="flex items-center gap-1">
          {onToggleExpand && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onToggleExpand}
            >
              {isExpanded ? (
                <Minimize2 className="size-3.5" />
              ) : (
                <Maximize2 className="size-3.5" />
              )}
            </Button>
          )}
          {isOwner && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="size-3" />
              Editar
            </Button>
          )}
          {isOwner && isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground"
              onClick={handleCancel}
              disabled={updateCreditsMutation.isPending}
            >
              <X className="size-3.5" />
              Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* ── Edit mode toolbar ────────────────────────────── */}
      {isEditing && (
        <div className="mx-3 sm:mx-5 mt-2 space-y-3 rounded-xl border border-border/50 bg-muted/25 p-3 sm:p-4">
          {/* Type segmented control */}
          {visibleTypes.length > 1 && (
            <div className="flex rounded-lg bg-muted/60 p-1">
              {visibleTypes.map((type) => {
                const isActive = selectedType === type;
                const Icon = type === "pf" ? User : type === "pm" ? Building2 : User;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedType(type)}
                    className={cn(
                      "relative flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-all duration-200",
                      isActive
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground/70",
                    )}
                  >
                    <Icon className="size-3.5 shrink-0" />
                    <span className="hidden sm:inline">{CREDIT_TYPE_LABELS[type]}</span>
                    <span className="sm:hidden">{CREDIT_TYPE_SHORT[type]}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Progress section */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                <span className="font-medium tabular-nums text-foreground">{totalAllocated.toLocaleString("es-MX")}</span>
                {" "}asignados
              </span>
              <span
                className={cn(
                  "text-xs font-semibold tabular-nums",
                  isOver
                    ? "text-red-600"
                    : remaining < orgTotal * 0.1
                      ? "text-amber-600"
                      : "text-emerald-600",
                )}
              >
                {remaining.toLocaleString("es-MX")} disponibles
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-300",
                  isOver
                    ? "bg-red-500"
                    : remaining < orgTotal * 0.1
                      ? "bg-amber-500"
                      : "bg-emerald-500",
                )}
                style={{ width: `${Math.min(ratio * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1.5 rounded-lg text-xs"
              onClick={handleDistributeEqually}
            >
              <Equal className="size-3" />
              Igualar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1.5 rounded-lg text-xs"
              onClick={handleReset}
            >
              <RotateCcw className="size-3" />
              Resetear
            </Button>
          </div>

          {/* Over-allocation alert */}
          {isOver && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <AlertCircle className="size-3.5 shrink-0" />
              Excedido por {Math.abs(remaining).toLocaleString("es-MX")} créditos
            </div>
          )}
        </div>
      )}

      {/* ── Search filter + role filter (view mode) ──────── */}
      {!isEditing && (
        <div className="mx-5 mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o correo..."
              className="h-8 w-full rounded-lg border border-border/60 bg-muted/30 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-border focus:bg-background transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            )}
          </div>
          {isExpanded && (
            <div className="flex gap-1">
              {(["all", "Owner", "Advisor"] as const).map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setRoleFilter(role)}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-medium transition-all",
                    roleFilter === role
                      ? "bg-foreground text-background"
                      : "bg-muted/40 text-muted-foreground hover:text-foreground/80",
                  )}
                >
                  {role === "all" ? "Todos" : role}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────── */}
      {advisors.length > 0 ? (
        <div className="relative flex-1 min-h-0">
          <div className="h-full overflow-y-auto px-3 sm:px-5 pt-2 pb-6">
          <table className="w-full">
            {/* Column headers */}
            <thead>
              <tr className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="pb-2 text-left font-medium">
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={() => handleSort("name")}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      Miembro
                      {sortBy === "name" ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        )
                      ) : (
                        <ArrowUpDown className="size-3 opacity-0 group-hover:opacity-100" />
                      )}
                    </button>
                  ) : (
                    "Miembro"
                  )}
                </th>
                <th className="hidden sm:table-cell pb-2 text-left font-medium w-20">Rol</th>
                {!isEditing && (
                  <>
                    <th className="pb-2 text-right font-medium w-16">
                      <button
                        type="button"
                        onClick={() => handleSort("pf")}
                        className="inline-flex items-center gap-1 hover:text-foreground transition-colors ml-auto"
                      >
                        PF
                        {sortBy === "pf" ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="size-3" />
                          ) : (
                            <ArrowDown className="size-3" />
                          )
                        ) : (
                          <ArrowUpDown className="size-3 opacity-30" />
                        )}
                      </button>
                    </th>
                    <th className="pb-2 text-right font-medium w-16">
                      <button
                        type="button"
                        onClick={() => handleSort("pm")}
                        className="inline-flex items-center gap-1 hover:text-foreground transition-colors ml-auto"
                      >
                        PM
                        {sortBy === "pm" ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="size-3" />
                          ) : (
                            <ArrowDown className="size-3" />
                          )
                        ) : (
                          <ArrowUpDown className="size-3 opacity-30" />
                        )}
                      </button>
                    </th>
                  </>
                )}
                {isEditing && (
                  <th className="pb-2 text-right font-medium">
                    {CREDIT_TYPE_SHORT[selectedType]}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {/* ── Unallocated credits summary row (view mode) ── */}
              {!isEditing && (unallocated.pf > 0 || unallocated.pm > 0) && (
                <tr className="bg-muted/30">
                  <td className="py-2 pr-3">
                    <span className="text-[11px] font-medium text-muted-foreground">
                      Sin asignar
                    </span>
                  </td>
                  <td className="hidden sm:table-cell py-2" />
                  <td className="py-2 text-right">
                    {unallocated.pf > 0 ? (
                      <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                        {unallocated.pf.toLocaleString("es-MX")}
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {unallocated.pm > 0 ? (
                      <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                        {unallocated.pm.toLocaleString("es-MX")}
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground/40">—</span>
                    )}
                  </td>
                </tr>
              )}

              {(isEditing ? advisors : sortedAdvisors).map((adv) => {
                const pfVal = adv.credits.pf ?? 0;
                const pmVal = adv.credits.pm ?? 0;
                const isClickable = !isEditing && !!onMemberClick;

                return (
                  <tr
                    key={adv.id}
                    className={cn(
                      "group",
                      isClickable && "cursor-pointer hover:bg-muted/40 transition-colors",
                    )}
                    onClick={isClickable ? () => onMemberClick(adv.email) : undefined}
                  >
                    {/* Name + Email */}
                    <td className="py-2.5 pr-3">
                      <div className="min-w-0">
                        <span className="truncate text-sm font-medium text-foreground block">
                          {adv.name} {adv.first_surname}
                        </span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {adv.email}
                        </span>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="hidden sm:table-cell py-2.5">
                      {adv.role && (
                        <span
                          className={cn(
                            "inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none",
                            adv.role === "Owner"
                              ? "border border-amber-200 bg-amber-50 text-amber-700"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {adv.role}
                        </span>
                      )}
                    </td>

                    {/* View mode: PF, PM columns */}
                    {!isEditing && (
                      <>
                        <td className="py-2.5 text-right">
                          {pfVal > 0 ? (
                            <span className="text-xs font-medium tabular-nums text-foreground">
                              {pfVal.toLocaleString("es-MX")}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">—</span>
                          )}
                        </td>
                        <td className="py-2.5 text-right">
                          {pmVal > 0 ? (
                            <span className="text-xs font-medium tabular-nums text-foreground">
                              {pmVal.toLocaleString("es-MX")}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">—</span>
                          )}
                        </td>
                      </>
                    )}

                    {/* Edit mode: stepper column */}
                    {isEditing && (
                      <td className="py-2.5 text-right">
                        <CellStepper
                          value={
                            allocations[adv.id]?.[selectedType] ?? 0
                          }
                          onChange={(v: number) =>
                            handleAllocationChange(adv.id, v)
                          }
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />
        </div>
      ) : (
        <div className="mx-5 mt-2 mb-5 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/20 py-8 text-muted-foreground">
          <Users className="size-5 stroke-[1.5]" />
          <span className="text-xs">No hay asesores en tu organización</span>
        </div>
      )}

      {/* ── Save button (edit mode) ──────────────────────── */}
      {isEditing && (
        <div className="border-t border-border/40 px-5 py-3">
          <Button
            onClick={handleSave}
            disabled={hasAnyOverAllocation || updateCreditsMutation.isPending}
            className="w-full"
            size="sm"
          >
            {updateCreditsMutation.isPending ? (
              <>
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Check className="mr-1.5 size-3.5" />
                Guardar distribución
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default TeamSection;
